import React, { useState } from 'react';
import { ErrorMessage, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  CustomField,
  FormGroup,
  Grid,
  Loading,
  Switch,
} from '@mss/mss-component-library';
import { LabeledValue } from 'antd/lib/select';
import moment from 'moment';
import { InputNumber } from 'formik-antd/lib/input-number';
import { Select } from 'formik-antd/lib/select';
import { Input } from 'formik-antd/lib/input';
import { DatePicker } from 'formik-antd/lib/date-picker';

type Props = {
  data: CustomField[];
  formIndex: any;
  disabled: any;
  enterNextStep: any;
  filterOption: any;
  users: LabelOptions[];
  isLoading?: boolean;
  createActions?: (
    fieldName: string,
    isCustomField?: boolean
  ) => React.ReactNode | undefined;
};
type LabelOptions = {
  label: string;
  options: LabeledValue[];
};

const { Option, OptGroup } = Select;

function getDataTaFromFieldName(fieldName: string) {
  const dataTa = `asset${fieldName}Input`;
  return dataTa.replaceAll(' ', '');
}

const isInt = (n: any) => {
  if (!isNaN(parseInt(n)) && isFinite(n)) {
    return parseInt(n) === parseFloat(n);
  }
  return false;
};

const CustomFields: React.FC<Props> = ({
  data,
  formIndex,
  disabled,
  enterNextStep,
  filterOption,
  users,
  createActions,
  isLoading,
}) => {
  const [t] = useTranslation(['common', 'asset']);
  const { values, handleBlur, setFieldTouched, handleChange, setFieldValue } =
    useFormikContext<any>();
  const { values: asset } = values.assets[formIndex];
  const [checked, setChecked] = useState({ default: false } as {
    [key: string]: boolean;
  });

  data.forEach(customField => {
    if (customField.field_type === 'boolean' && customField.required) {
      if (!('custom_field_values' in asset)) {
        asset['custom_field_values'] = {};
      }
      if (!(customField.slug in asset.custom_field_values)) {
        asset.custom_field_values[customField.slug] = false;
        setChecked({
          ...checked,
          [customField.slug]: false,
        });
      }
    }
  });
  const rows = data.map(custom_field => {
    const fieldName = `assets.${formIndex}.values.custom_field_values.${custom_field.slug}`;
    return custom_field.choices.length > 0 ? (
      <Grid.Col width={4} key={`cf${custom_field.id}`}>
        <FormGroup
          required={custom_field.required}
          label={t(custom_field.name)}
          actions={createActions?.(custom_field.slug, true)}
          input={
            <Select
              disabled={disabled}
              name={fieldName}
              placeholder={
                custom_field.placeholder || `${t('Please select one')}...`
              }
              showSearch
              optionFilterProp={'children'}
              filterOption={filterOption}
              style={{ width: '100%' }}
              value={asset?.custom_field_values?.[custom_field.slug] ?? null}
              onInputKeyDown={() => setFieldTouched(fieldName, true, true)}
              onBlur={handleBlur}
              onChange={handleChange}
              data-ta={getDataTaFromFieldName(custom_field.name)}
              onKeyDown={enterNextStep}
            >
              {Object.entries(custom_field.choices).map((x: [string, any]) => {
                const [key, value] = x;
                return (
                  <Select.Option key={key} value={value}>
                    {value}
                  </Select.Option>
                );
              })}
            </Select>
          }
          error={<ErrorMessage name={fieldName} />}
        />
      </Grid.Col>
    ) : custom_field.field_type === 'integer' ? (
      <Grid.Col width={4} key={`cf${custom_field.id}`}>
        <FormGroup
          required={custom_field.required}
          label={t(custom_field.name)}
          actions={createActions?.(custom_field.slug, true)}
          input={
            <InputNumber
              type={'number'}
              disabled={disabled}
              name={fieldName}
              placeholder={custom_field.placeholder || t(custom_field.name)}
              value={
                isInt(asset?.custom_field_values?.[custom_field.slug])
                  ? asset?.custom_field_values?.[custom_field.slug]
                  : ''
              }
              onInput={() => {
                setFieldTouched(fieldName, true, false);
              }}
              onBlur={() => {
                setFieldTouched(fieldName, true, true);
              }}
              onKeyPress={enterNextStep}
              data-ta={getDataTaFromFieldName(custom_field.name)}
              style={{ width: '100%' }}
            />
          }
          error={<ErrorMessage name={fieldName} />}
        />
      </Grid.Col>
    ) : custom_field.field_type === 'user_group' ? (
      <Grid.Col width={4} key={`cf${custom_field.id}`}>
        <FormGroup
          required={custom_field.required}
          label={t(custom_field.name)}
          actions={createActions?.(custom_field.slug, true)}
          input={
            <Select
              disabled={disabled}
              name={fieldName}
              placeholder={
                custom_field.placeholder || `${t('Please a user / group')}...`
              }
              showSearch
              optionFilterProp={'children'}
              style={{ width: '100%' }}
              value={asset?.custom_field_values?.[custom_field.slug]?.id}
              onInputKeyDown={() => setFieldTouched(fieldName, true, true)}
              onBlur={handleBlur}
              data-ta={getDataTaFromFieldName(custom_field.name)}
              onKeyDown={enterNextStep}
            >
              {users && users.length > 0 ? (
                users.map(group => {
                  if (group.options.length > 0) {
                    return (
                      <OptGroup
                        key={`${group.label}-sel-${custom_field.id}`}
                        label={group.label}
                      >
                        {(group.options ?? []).map((x: any) => {
                          return (
                            <Option key={x.key} value={x.value}>
                              {x.label}
                            </Option>
                          );
                        })}
                      </OptGroup>
                    );
                  } else {
                    return <></>;
                  }
                })
              ) : (
                <></>
              )}
            </Select>
          }
          error={<ErrorMessage name={fieldName} />}
        />
      </Grid.Col>
    ) : ['string', 'ip_address'].indexOf(custom_field.field_type) >= 0 ? (
      <Grid.Col width={4} key={`cf${custom_field.id}`}>
        <FormGroup
          required={custom_field.required}
          label={t(custom_field.name)}
          actions={createActions?.(custom_field.slug, true)}
          input={
            <Input
              maxLength={250}
              disabled={disabled}
              name={fieldName}
              placeholder={custom_field.placeholder || t(custom_field.name)}
              value={asset?.custom_field_values?.[custom_field.slug] ?? ''}
              onInput={() => setFieldTouched(fieldName, true, true)}
              onBlur={handleBlur}
              onChange={handleChange}
              data-ta={getDataTaFromFieldName(custom_field.name)}
              onKeyPress={enterNextStep}
            />
          }
          error={<ErrorMessage name={fieldName} />}
        />
      </Grid.Col>
    ) : custom_field.field_type === 'date_time' ? (
      <Grid.Col width={4} key={`cf${custom_field.id}`}>
        <FormGroup
          label={t(custom_field.name)}
          required={custom_field.required}
          actions={createActions?.(custom_field.slug, true)}
          input={
            <DatePicker
              style={{ width: '100%' }}
              disabled={disabled}
              name={fieldName}
              placeholder={
                custom_field.placeholder || t('Select a date / time')
              }
              value={
                asset?.custom_field_values?.[custom_field.slug]
                  ? moment(asset.custom_field_values[custom_field.slug])
                  : undefined
              }
              onChange={(value, date) => {
                asset.custom_field_values[custom_field.slug] = value;
                setFieldValue(fieldName, date);
                setFieldTouched(fieldName, true, true);
              }}
              onBlur={handleBlur}
              data-ta={getDataTaFromFieldName(custom_field.name)}
              showTime={true}
            />
          }
          error={
            <ErrorMessage name={fieldName} render={() => t('Required')} />
          }
        />
      </Grid.Col>
    ) : custom_field.field_type === 'boolean' ? (
      <Grid.Col width={4} key={`cf${custom_field.id}`}>
        <FormGroup
          required={custom_field.required}
          label={t(custom_field.name)}
          actions={createActions?.(custom_field.slug, true)}
          input={
            <Switch
              data-ta={getDataTaFromFieldName(custom_field.name)}
              defaultChecked={false}
              disabled={disabled}
              name={fieldName}
              value={asset?.custom_field_values?.[custom_field.slug] ?? false}
              checked={
                checked[custom_field.slug] ??
                asset?.custom_field_values?.[custom_field.slug] ??
                false
              }
              onChange={() => {
                if (!('custom_field_values' in asset)) {
                  asset['custom_field_values'] = {};
                  asset.custom_field_values[custom_field.slug] = false;
                }
                asset.custom_field_values[custom_field.slug] =
                  !asset.custom_field_values[custom_field.slug];
                setChecked({
                  ...checked,
                  [custom_field.slug]:
                    asset.custom_field_values[custom_field.slug],
                });
                setFieldTouched(fieldName, true, true);
              }}
              onKeyDown={enterNextStep}
            />
          }
          error={<ErrorMessage name={fieldName} />}
        />
      </Grid.Col>
    ) : null;
  });

  return isLoading ? <Loading /> : <>{rows}</>;
};

export default CustomFields;
